import type { Database } from 'sql.js';
import { newPrefixedPublicId } from '@core/domain/secureRandom.ts';
import {
  insertRowId,
  persistBrowserDataUrl,
  queryOne,
} from '@core/infrastructure/driven/sqljs/sqljsSqlitePrimitives.ts';
import {
  assertRedencionParticipanteAllowed,
  getGruposAlimentosIdsForProveedor,
} from '@core/infrastructure/driven/sqljs/sqliteEntregaClaimConstraints.ts';
import type {
  RedencionBonoHeader,
  RedencionBonoParticipanteFormPayload,
} from '../../../domain/redencionBonoModel.ts';
import { resolveRedencionParticipanteBonoFk } from './mappers.ts';

/** Silently rolls back the current database transaction. */
function rollbackQuiet(db: Database): void {
  try {
    db.run('ROLLBACK');
  } catch {
    /* ignore */
  }
}

/** Checks if a participant exists in the catalog. */
function participanteCatalogRowExists(db: Database, participanteId: number): boolean {
  return queryOne(db, 'SELECT id FROM participantes WHERE id = ? LIMIT 1', [participanteId]) != null;
}

const INSERT_PARTICIPANTE_SQL = `INSERT INTO redencion_bono_participante (
        public_id, redencion_bono_id, participante_id,
        persona_autorizada, nombre_persona_autorizada,
        bono_id, tipo_bono, politica_tratamientos, firma_base64,
        registro_fotografico, observaciones
      ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`;

/** Inserts a participant row (`public_id` = `rbp_<uuid>` opaque id). */
function insertParticipanteRow(
  db: Database,
  redencionId: number,
  line: RedencionBonoParticipanteFormPayload,
): void {
  if (!participanteCatalogRowExists(db, line.participanteId)) return;
  const firmaPath = persistBrowserDataUrl(line.firmaBase64);
  const fotoPath = persistBrowserDataUrl(line.registroFotografico);
  const { bonoId, tipoBono } = resolveRedencionParticipanteBonoFk(db, line);
  const nombreAutorizada = line.personaAutorizada ? String(line.nombrePersonaAutorizada ?? '').trim() : '';
  insertRowId(db, INSERT_PARTICIPANTE_SQL, [
    newPrefixedPublicId('rbp'),
    redencionId,
    line.participanteId,
    line.personaAutorizada ? 1 : 0,
    nombreAutorizada,
    bonoId,
    tipoBono,
    line.politicaTratamientos ? 1 : 0,
    firmaPath,
    fotoPath,
    line.observaciones ?? '',
  ]);
  db.run(`UPDATE participantes SET fecha_actualizacion = datetime('now') WHERE id = ?`, [line.participanteId]);
}

/** Replaces all participants for a given redemption. */
function replaceParticipantes(
  db: Database,
  redencionId: number,
  participantes: RedencionBonoParticipanteFormPayload[],
  headerProveedorId: number | null,
  entregaCatalogId: number | null,
): void {
  const headerProv = headerProveedorId != null && headerProveedorId > 0 ? headerProveedorId : null;
  const entregaId = entregaCatalogId != null && entregaCatalogId > 0 ? entregaCatalogId : null;
  const headerGrps = headerProv == null ? [] : getGruposAlimentosIdsForProveedor(db, headerProv);

  for (const line of participantes) {
    if (!participanteCatalogRowExists(db, line.participanteId)) continue;
    if (headerGrps.length > 0) {
      for (const grpId of headerGrps) {
        assertRedencionParticipanteAllowed(db, {
          redencionBonoId: redencionId,
          entregaCatalogId: entregaId,
          participanteId: line.participanteId,
          proveedorId: headerProv,
          grupoAlimentosId: grpId,
        });
      }
    } else {
      assertRedencionParticipanteAllowed(db, {
        redencionBonoId: redencionId,
        entregaCatalogId: entregaId,
        participanteId: line.participanteId,
        proveedorId: headerProv,
        grupoAlimentosId: null,
      });
    }
  }

  db.run('DELETE FROM redencion_bono_participante WHERE redencion_bono_id = ?', [redencionId]);
  for (const line of participantes) {
    insertParticipanteRow(db, redencionId, line);
  }
}

/** Inserts a new redemption header (`public_id` = `rb_<uuid>`) and its participants. */
export function insertRedencionWithParticipantes(
  db: Database,
  h: RedencionBonoHeader,
  participantes: RedencionBonoParticipanteFormPayload[],
  schedulePersist: () => void,
): number {
  const pid = h.proveedorId != null && h.proveedorId > 0 ? h.proveedorId : null;
  const eid = h.entregaId != null && h.entregaId > 0 ? h.entregaId : null;
  const rid = h.responsableId != null && h.responsableId > 0 ? h.responsableId : null;
  db.run('BEGIN IMMEDIATE');
  try {
    const id = insertRowId(
      db,
      `INSERT INTO redenciones_bono (public_id, entrega_id, es_remanente, fecha_hora, proveedor_id, responsable_id, latitud, longitud) VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        newPrefixedPublicId('rb'),
        eid,
        h.esRemanente === true ? 1 : 0,
        h.fecha.trim(),
        pid,
        rid,
        String(h.latitud ?? '').trim(),
        String(h.longitud ?? '').trim(),
      ],
    );
    replaceParticipantes(db, id, participantes, pid, eid);
    db.run('COMMIT');
    schedulePersist();
    return id;
  } catch (e) {
    rollbackQuiet(db);
    throw e;
  }
}

/** Updates an existing redemption and its participants. */
export function updateRedencionWithParticipantes(
  db: Database,
  id: number,
  h: RedencionBonoHeader,
  participantes: RedencionBonoParticipanteFormPayload[],
  schedulePersist: () => void,
): void {
  const pid = h.proveedorId != null && h.proveedorId > 0 ? h.proveedorId : null;
  const eid = h.entregaId != null && h.entregaId > 0 ? h.entregaId : null;
  const rid = h.responsableId != null && h.responsableId > 0 ? h.responsableId : null;
  db.run('BEGIN IMMEDIATE');
  try {
    db.run(
      `UPDATE redenciones_bono SET entrega_id = ?, es_remanente = ?, fecha_hora = ?, proveedor_id = ?, responsable_id = ?, latitud = ?, longitud = ?, fecha_actualizacion = datetime('now') WHERE id = ?`,
      [
        eid,
        h.esRemanente === true ? 1 : 0,
        h.fecha.trim(),
        pid,
        rid,
        String(h.latitud ?? '').trim(),
        String(h.longitud ?? '').trim(),
        id,
      ],
    );
    replaceParticipantes(db, id, participantes, pid, eid);
    db.run('COMMIT');
    schedulePersist();
  } catch (e) {
    rollbackQuiet(db);
    throw e;
  }
}
